import type { CSSProperties } from 'react';
import type { ThemeContextValue } from '@course-authoring/shared-types';
import { adminButton, adminField, adminFocusable, adminTokens as a } from '../../admin-ui';

type ThemeTokens = ThemeContextValue['tokens'];

/**
 * Shared inline styles for the Author with AI pages. The frame uses the admin
 * tokens; `tokens` (the course theme) is only read for the shared draft and
 * published colors and for video media.
 */
export function createAuthorWithAiStyles(tokens: ThemeTokens): Record<string, CSSProperties> {
  const button: CSSProperties = {
    ...adminButton.secondary,
    textDecoration: 'none',
  };
  const linkText: CSSProperties = {
    ...adminFocusable,
    padding: 0,
    border: 'none',
    background: 'none',
    fontFamily: 'inherit',
    color: a.brand.roseForeground,
    cursor: 'pointer',
  };
  return {
    // ── Page frames ──
    // Full-width admin canvas with the content held to a centered column.
    page: {
      flex: '1 0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xl,
      width: '100%',
      padding: `${a.spacing['2xl']} max(${a.layout.pageGutterNarrow}, calc((100% - 1040px) / 2)) 48px`,
      backgroundColor: a.surface.canvas,
      color: a.text.primary,
      boxSizing: 'border-box',
    },
    centered: {
      flex: '1 0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.md,
      alignItems: 'flex-start',
      width: '100%',
      padding: `40px max(${a.layout.pageGutterNarrow}, calc((100% - 560px) / 2))`,
      backgroundColor: a.surface.canvas,
      color: a.text.primary,
      boxSizing: 'border-box',
    },
    heading: {
      margin: 0,
      fontSize: a.typography.pageTitle.size,
      lineHeight: a.typography.pageTitle.lineHeight,
      fontWeight: a.typography.pageTitle.weight,
      color: a.text.primary,
    },
    subheading: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
      color: a.text.primary,
    },
    // "or search all questions" after the reply chips: plain lead, link-styled button.
    searchLinkLead: {
      fontSize: '13px',
      color: a.text.secondary,
      whiteSpace: 'nowrap',
    },
    searchLink: {
      ...linkText,
      fontSize: '13px',
      fontWeight: 500,
      textDecoration: 'underline',
      cursor: 'pointer',
      whiteSpace: 'nowrap',
    },
    muted: {
      margin: 0,
      fontSize: '13px',
      lineHeight: 1.5,
      color: a.text.secondary,
    },
    error: {
      margin: 0,
      fontSize: '13px',
      lineHeight: 1.5,
      color: a.status.error.text,
    },
    banner: {
      padding: `${a.spacing.md} ${a.spacing.lg}`,
      borderRadius: a.radius.control,
      border: `1px solid ${a.status.info.border}`,
      backgroundColor: a.status.info.background,
      color: a.text.primary,
      fontSize: '14px',
    },
    errorBanner: {
      padding: `${a.spacing.md} ${a.spacing.lg}`,
      borderRadius: a.radius.control,
      border: `1px solid ${a.status.error.border}`,
      backgroundColor: a.status.error.background,
      color: a.status.error.text,
      fontSize: '14px',
    },

    // ── Buttons ──
    button,
    dangerButton: adminButton.danger,
    buttonDisabled: adminButton.disabled,
    iconButton: {
      ...adminFocusable,
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: a.radius.pill,
      border: `1px solid ${a.border.control}`,
      backgroundColor: a.surface.panel,
      color: a.text.secondary,
      cursor: 'pointer',
      padding: 0,
      flexShrink: 0,
    },
    iconButtonActive: {
      color: a.text.onPrimary,
      backgroundColor: a.action.primary,
      borderColor: a.action.primary,
    },

    // ── Landing ──
    groups: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.lg,
      marginBottom: a.spacing.lg,
    },
    group: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.md,
    },
    groupHeading: {
      margin: 0,
      fontSize: '17px',
      fontWeight: 500,
      color: a.text.primary,
    },
    cards: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: a.spacing.md,
    },
    card: {
      ...adminFocusable,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: a.spacing.sm,
      padding: `20px ${a.spacing.xl}`,
      borderRadius: a.radius.panel,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      boxShadow: a.shadow.panel,
      textAlign: 'left',
      cursor: 'pointer',
      color: a.text.primary,
      font: 'inherit',
    },
    cardDisabled: {
      cursor: 'not-allowed',
      opacity: 0.6,
      boxShadow: 'none',
    },
    cardIcon: {
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: a.radius.well,
      backgroundColor: a.surface.rose,
      color: a.brand.roseForeground,
      fontSize: '16px',
    },
    cardTitle: {
      margin: 0,
      fontSize: '17px',
      fontWeight: 600,
    },
    cardBody: {
      margin: 0,
      fontSize: '15px',
      lineHeight: 1.5,
      color: a.text.secondary,
    },
    notAvailable: {
      fontSize: '12px',
      fontWeight: 600,
      color: a.text.secondary,
    },
    orBar: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.md,
      color: a.text.secondary,
      fontSize: '13px',
    },
    orLine: {
      flex: 1,
      height: '1px',
      backgroundColor: a.border.subtle,
    },
    askBar: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      padding: `${a.spacing.sm} ${a.spacing.sm} ${a.spacing.sm} ${a.spacing.lg}`,
      borderRadius: '28px',
      border: `1px solid ${a.border.control}`,
      backgroundColor: a.surface.panel,
      boxShadow: a.shadow.panel,
    },
    askField: {
      position: 'relative',
      flex: 1,
      display: 'flex',
      alignItems: 'center',
      minWidth: 0,
    },
    askInput: {
      ...adminFocusable,
      width: '100%',
      border: 'none',
      outline: 'none',
      fontSize: '15px',
      padding: `${a.spacing.sm} 0`,
      backgroundColor: 'transparent',
      color: a.text.primary,
      fontFamily: 'inherit',
    },
    askGhost: {
      position: 'absolute',
      left: 0,
      right: 0,
      paddingRight: a.spacing.sm,
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      color: a.text.secondary,
      fontSize: '14px',
      pointerEvents: 'none',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
    },
    askExample: {
      ...adminFocusable,
      pointerEvents: 'auto',
      padding: 0,
      border: 'none',
      background: 'none',
      fontFamily: 'inherit',
      color: a.brand.roseForeground,
      fontSize: '14px',
      cursor: 'pointer',
      textDecoration: 'underline',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    spark: {
      display: 'inline-flex',
      alignItems: 'center',
      color: a.brand.roseForeground,
      fontSize: '16px',
      flexShrink: 0,
    },
    altRow: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      flexWrap: 'wrap',
    },
    chip: {
      ...adminFocusable,
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      padding: '6px 12px',
      fontFamily: 'inherit',
      fontSize: '13px',
      fontWeight: 400,
      border: `1px solid ${a.border.control}`,
      borderRadius: a.radius.pill,
      background: a.surface.panel,
      color: a.text.primary,
      cursor: 'pointer',
    },

    // ── Lists ──
    lists: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xl,
      padding: a.spacing.xl,
      borderRadius: a.radius.panel,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      boxShadow: a.shadow.panel,
    },
    listSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
    },
    list: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      borderTop: `1px solid ${a.border.subtle}`,
    },
    row: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.md,
      padding: `${a.spacing.sm} ${a.spacing.xs}`,
      borderBottom: `1px solid ${a.border.subtle}`,
      fontSize: '14px',
      minHeight: '40px',
    },
    origin: {
      width: '20px',
      display: 'inline-flex',
      justifyContent: 'center',
      color: a.text.secondary,
      flexShrink: 0,
    },
    outputId: {
      ...adminFocusable,
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '13px',
      color: a.brand.roseForeground,
      textDecoration: 'none',
      flexShrink: 0,
    },
    rowTitle: {
      ...adminFocusable,
      flex: 1,
      minWidth: 0,
      color: a.text.primary,
      textDecoration: 'none',
      fontWeight: 500,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    when: {
      color: a.text.secondary,
      fontSize: '13px',
      flexShrink: 0,
      whiteSpace: 'nowrap',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.xs,
      padding: `2px ${a.spacing.sm}`,
      borderRadius: a.radius.pill,
      fontSize: '12px',
      fontWeight: 600,
      backgroundColor: a.surface.subtle,
      color: a.text.secondary,
      whiteSpace: 'nowrap',
      flexShrink: 0,
    },
    badgeDraft: {
      backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowDraft} 18%, transparent)`,
      color: a.text.primary,
    },
    badgePublished: {
      backgroundColor: `color-mix(in srgb, ${tokens.ui.workflowPublished} 18%, transparent)`,
      color: a.text.primary,
    },
    badgeFailed: {
      backgroundColor: a.status.error.background,
      color: a.status.error.text,
    },
    badgeBuilding: {
      backgroundColor: a.status.info.background,
      color: a.status.info.text,
    },
    showMore: {
      ...linkText,
      alignSelf: 'flex-start',
      padding: `${a.spacing.xs} 0`,
      fontSize: '13px',
      fontWeight: 600,
      cursor: 'pointer',
    },

    // ── Conversation ──
    screen: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      minHeight: 0,
      color: a.text.primary,
    },
    screenHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.md,
      padding: `${a.spacing.md} ${a.spacing.xl}`,
      borderBottom: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
    },
    backLink: {
      ...adminFocusable,
      color: a.text.primary,
      textDecoration: 'none',
      fontSize: '14px',
      fontWeight: 400,
      whiteSpace: 'nowrap',
    },
    screenTitle: {
      margin: 0,
      fontSize: '16px',
      fontWeight: 600,
      color: a.text.primary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    titleGroup: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
      flex: 1,
    },
    menu: {
      position: 'relative',
    },
    menuPopover: {
      position: 'absolute',
      right: 0,
      top: `calc(100% + ${a.spacing.xs})`,
      minWidth: '200px',
      padding: a.spacing.xs,
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.subtle}`,
      borderRadius: a.radius.control,
      boxShadow: a.shadow.overlay,
      zIndex: 20,
    },
    menuItem: {
      ...adminFocusable,
      width: '100%',
      fontFamily: 'inherit',
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      border: 'none',
      background: 'none',
      textAlign: 'left',
      fontSize: '14px',
      color: a.status.error.text,
      cursor: 'pointer',
      borderRadius: a.radius.control,
    },
    thread: {
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      backgroundColor: a.surface.canvas,
      color: a.text.primary,
    },
    /** Sits between the header and the thread, left-aligned with the screen title. */
    threadBack: {
      padding: `${a.spacing.md} ${a.spacing.xl} 0`,
      backgroundColor: a.surface.canvas,
    },
    threadInner: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xl,
      minHeight: '100%',
      maxWidth: '760px',
      margin: '0 auto',
      padding: `${a.spacing.xl} ${a.spacing.xl} ${a.spacing.lg}`,
      boxSizing: 'border-box',
    },
    /** Pushes a short thread down against the composer (auto margin, not flex-end, so overflow still scrolls). */
    threadSpacer: {
      marginTop: 'auto',
    },
    messageRow: {
      display: 'flex',
      gap: a.spacing.md,
      alignItems: 'flex-start',
    },
    messageRowAuthor: {
      flexDirection: 'row-reverse',
    },
    avatar: {
      width: '36px',
      height: '36px',
      borderRadius: a.radius.pill,
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      fontSize: '13px',
      fontWeight: 600,
    },
    avatarAssistant: {
      backgroundColor: a.surface.panel,
      border: `1px solid ${a.border.subtle}`,
      boxSizing: 'border-box',
      fontSize: '17px',
      color: a.brand.roseForeground,
    },
    avatarAuthor: {
      backgroundColor: a.action.primary,
      color: a.text.onPrimary,
    },
    messageBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      minWidth: 0,
      flex: 1,
      maxWidth: 'calc(100% - 48px)',
      fontSize: '15px',
      lineHeight: 1.55,
      color: a.text.primary,
    },
    messageBodyAuthor: {
      alignItems: 'flex-end',
    },
    authorBubble: {
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      borderRadius: a.radius.tile,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: 'rgba(255, 255, 255, 0.7)',
      whiteSpace: 'pre-wrap',
    },
    fileTile: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      whiteSpace: 'normal',
    },
    fileTileText: {
      display: 'inline-flex',
      flexDirection: 'column',
      gap: '2px',
    },
    fileMeta: {
      fontSize: '13px',
      color: a.text.secondary,
    },
    paragraph: {
      margin: 0,
      whiteSpace: 'pre-wrap',
    },
    bulletList: {
      margin: 0,
      paddingLeft: a.spacing.xl,
    },
    tile: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      borderRadius: a.radius.control,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      maxWidth: '520px',
    },
    tileItem: {
      display: 'flex',
      gap: a.spacing.sm,
      alignItems: 'baseline',
    },
    approvalLine: {
      fontSize: '14px',
      color: a.text.secondary,
      display: 'inline-flex',
      gap: a.spacing.xs,
      alignItems: 'center',
    },
    card2: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      padding: a.spacing.lg,
      borderRadius: a.radius.control,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      maxWidth: '640px',
    },
    noticeCard: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      padding: a.spacing.lg,
      borderRadius: a.radius.control,
      border: `1px solid ${a.status.error.border}`,
      backgroundColor: a.status.error.background,
      maxWidth: '640px',
    },
    waiting: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      color: a.text.secondary,
      fontSize: '15px',
    },
    actionsRow: {
      display: 'flex',
      gap: a.spacing.sm,
      flexWrap: 'wrap',
      alignItems: 'center',
    },
    /** "Open in editor" links in the drafts-ready card: brand rose, matching the ask-bar example link. */
    resultLink: {
      color: a.brand.roseForeground,
    },
    progressList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
      fontSize: '14px',
    },
    progressRow: {
      display: 'flex',
      gap: a.spacing.md,
      justifyContent: 'space-between',
    },
    progressStatus: {
      color: a.text.secondary,
      whiteSpace: 'nowrap',
    },

    // ── Outline box ──
    outline: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
      padding: a.spacing.lg,
      borderRadius: a.radius.control,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      maxWidth: '640px',
    },
    outlineHeader: {
      fontSize: '12px',
      fontWeight: 600,
      letterSpacing: '0.02em',
      textTransform: 'uppercase',
      color: a.text.secondary,
    },
    outlineTitleRow: {
      display: 'flex',
      alignItems: 'center',
      gap: a.spacing.sm,
      justifyContent: 'space-between',
    },
    outlineTitle: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
      color: a.text.primary,
    },
    outlineRows: {
      margin: 0,
      paddingLeft: a.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
      fontSize: '14px',
    },
    outlineRow: {
      padding: `2px ${a.spacing.xs}`,
      borderRadius: '4px',
    },
    outlineRowAdded: {
      backgroundColor: a.status.success.background,
    },
    outlineRowContent: {
      display: 'flex',
      gap: a.spacing.sm,
      justifyContent: 'space-between',
      alignItems: 'baseline',
    },
    blockName: {
      fontSize: '12px',
      color: a.text.secondary,
      whiteSpace: 'nowrap',
    },
    markTag: {
      fontSize: '11px',
      fontWeight: 600,
      padding: `0 ${a.spacing.xs}`,
      borderRadius: '4px',
      backgroundColor: a.status.info.background,
      color: a.text.primary,
      marginLeft: a.spacing.xs,
    },
    approvedTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '12px',
      fontWeight: 600,
      color: a.status.success.text,
      whiteSpace: 'nowrap',
    },
    lessonDescription: {
      fontSize: '13px',
      color: a.text.secondary,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    outlineFooter: {
      fontSize: '13px',
      color: a.text.secondary,
    },
    removedList: {
      margin: 0,
      padding: 0,
      listStyle: 'none',
      fontSize: '13px',
      color: a.text.secondary,
      textDecoration: 'line-through',
    },

    // ── Composer ──
    composerArea: {
      borderTop: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      padding: `${a.spacing.md} ${a.spacing.xl} ${a.spacing.lg}`,
    },
    composerInner: {
      maxWidth: '760px',
      margin: '0 auto',
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.sm,
    },
    composer: {
      display: 'flex',
      gap: a.spacing.sm,
      alignItems: 'flex-end',
      padding: a.spacing.sm,
      borderRadius: '28px',
      border: `1px solid ${a.border.control}`,
      backgroundColor: a.surface.panel,
    },
    /** Subtle replacement for the browser focus ring on the ask bar and the composer. */
    barFocused: {
      borderColor: a.text.disabled,
    },
    /** The finished-conversation bar: a message in place of the field. */
    composerFinished: {
      border: '1px solid transparent',
      alignItems: 'center',
      minHeight: '56px',
      boxSizing: 'border-box',
      // 712px inner width lines the bar up with the thread column; 47px + the 1px border
      // equals the 36px avatar plus its 12px gap, so the text starts where message text does.
      padding: `${a.spacing.sm} ${a.spacing.xl} ${a.spacing.sm} 47px`,
      fontSize: '15px',
      color: a.text.secondary,
    },
    composerInput: {
      ...adminFocusable,
      flex: 1,
      minHeight: '40px',
      maxHeight: '30vh',
      resize: 'none',
      border: 'none',
      outline: 'none',
      fontSize: '15px',
      lineHeight: 1.5,
      padding: a.spacing.sm,
      fontFamily: 'inherit',
      color: a.text.primary,
      backgroundColor: 'transparent',
      boxSizing: 'border-box',
    },
    composerFooter: {
      margin: 0,
      fontSize: '13px',
      color: a.text.secondary,
    },

    // ── Pickers ──
    modalHeader: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: a.spacing.md,
      padding: a.spacing.xl,
      borderBottom: `1px solid ${a.border.subtle}`,
    },
    modalBody: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.md,
      padding: a.spacing.xl,
      overflowY: 'auto',
      minHeight: 0,
    },
    modalFooter: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: a.spacing.sm,
      padding: `${a.spacing.md} ${a.spacing.xl}`,
      borderTop: `1px solid ${a.border.subtle}`,
    },
    // Video picker rows: poster + text inside the same one-card list.
    vpRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '14px',
      padding: '12px 18px',
      borderBottom: `1px solid ${a.surface.subtle}`,
    },
    vpText: {
      ...adminFocusable,
      flex: 1,
      minWidth: 0,
      textAlign: 'left',
      cursor: 'pointer',
      backgroundColor: 'transparent',
      border: 'none',
      padding: 0,
      font: 'inherit',
      fontSize: '14px',
      lineHeight: 1.45,
      color: a.text.primary,
    },
    pickList: {
      listStyle: 'none',
      margin: 0,
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
    },
    pickScroll: {
      position: 'relative',
    },
    questionOptions: {
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.xs,
      maxWidth: '640px',
    },
    pickRowInert: {
      cursor: 'default',
    },
    pickRow: {
      ...adminFocusable,
      width: '100%',
      display: 'flex',
      gap: a.spacing.md,
      alignItems: 'flex-start',
      padding: `${a.spacing.sm} ${a.spacing.md}`,
      borderRadius: a.radius.control,
      border: `1px solid ${a.border.subtle}`,
      backgroundColor: a.surface.panel,
      textAlign: 'left',
      cursor: 'pointer',
      fontSize: '14px',
      color: a.text.primary,
      boxSizing: 'border-box',
      font: 'inherit',
    },
    pickMeta: {
      display: 'flex',
      gap: a.spacing.md,
      fontSize: '12px',
      color: a.text.secondary,
      marginTop: '2px',
    },
    fade: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: '48px',
      background: `linear-gradient(to bottom, transparent, ${a.surface.panel})`,
      pointerEvents: 'none',
    },
    scrollCue: {
      alignSelf: 'center',
      fontSize: '12px',
      color: a.text.secondary,
    },
    poster: {
      ...adminFocusable,
      position: 'relative',
      width: '112px',
      height: '63px',
      borderRadius: a.radius.control,
      overflow: 'hidden',
      backgroundColor: tokens.backgrounds.surfaceAlt,
      flexShrink: 0,
      border: 'none',
      padding: 0,
      cursor: 'pointer',
    },
    posterImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      display: 'block',
    },
    playBadge: {
      position: 'absolute',
      inset: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: tokens.text.onInverse,
      backgroundColor: `color-mix(in srgb, ${tokens.backgrounds.inverse} 35%, transparent)`,
      fontSize: '16px',
    },
    scenarioDescription: {
      display: '-webkit-box',
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      fontSize: '13px',
      color: a.text.secondary,
      marginTop: '2px',
    },
    previewVideo: {
      width: '100%',
      maxHeight: '60vh',
      backgroundColor: tokens.backgrounds.inverse,
      display: 'block',
    },
  };
}
